import { Box, Button, CardMedia, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";
import { FC, useCallback, useEffect, useState } from "react";
import * as yup from "yup";
import useAccount from "../../../hooks/useAccount";
import ftrimage from "../../../assets/ftr_automation_new_color.png";
import LoadingButton from "@mui/lab/LoadingButton";
import { useCreateMultiFrameworkMutation } from "../../../generated";
import useSnackBars from "../../../hooks/useSnackbar";
import ConnectedAccount from "../../../models/ConnectedAccount";
import Account from "../../../models/Account";
import useNewrelic from "../../../hooks/useNewrelic";

interface CreateFTRWorkflowProps {
	arn: string;
	targetAccountType: string;
	setUnlockSteps: React.Dispatch<
		React.SetStateAction<{
			step2: boolean;
			step3: boolean;
			step4: boolean;
			step5: boolean;
		}>
	>;
	changeTab: (
		index: number,
		applicationName: string,
		targetArn: string,
		connectedAccounts: Array<ConnectedAccount>,
		accounttype: string,
	) => void;
}

const validationSchema = yup.object().shape({
	applicationName: yup
		.string()
		.matches(/^[a-zA-Z0-9\s\-]{1,64}$/, "Please enter a valid name")
		.required("Please enter a name for the Application")
});

const CreateFTRWorkflow: FC<CreateFTRWorkflowProps> = ({
	arn,
	targetAccountType,
	setUnlockSteps,
	changeTab,
}: CreateFTRWorkflowProps) => {
	const { accounts, connectedAccounts } = useAccount();
	const { noticeError } = useNewrelic();
	const { setAlerts } = useSnackBars();
	const [technicalReviewArn, setTechnicalReviewArn] = useState(arn); //track technical review account that is selected
	const [connectedAccountsDropDown, setConnectedAccountsDropDown] =
		useState(connectedAccounts); //Displays the connected accounts dropdown
	const [technicalAccountType, setTechnicalAccountType] = useState(targetAccountType);
	const [accountIdRef, setAccountIdRef] = useState<string>("");
	const [connectedAutomatePlus, setConnectedAutomatePlus] = useState(true);
	const uniqueConnectedAccountIds = Array.from(
		new Set(connectedAccountsDropDown.map((a) => a.accountIdRef))
	);
	const [createMultiFrameworkMutation, { loading }] = useCreateMultiFrameworkMutation({
		context: {
			apiName: "well_architected",
		},
	});

	//FTR workflow steps displayed on the frontend
	const steps = [
		"Create Application",
		"Complete Non-Disruptive Recommendations",
		"Complete People, Policy & Tools Review",
		"Remediate Failed Controls",
		"Generate Reports"
	];

	//Filter and get the connected account
	const getFilteredConnectedAccounts = useCallback(() => {
		return connectedAccountsDropDown
			.filter((connectedAccount: ConnectedAccount) => connectedAccount.accountIdRef === accountIdRef)
			.map((connectedAccount: ConnectedAccount) => ({
				accountIdRef: connectedAccount.accountIdRef,
				region: connectedAccount.region,
			}));
	}, [connectedAccountsDropDown, accountIdRef]);

	useEffect(() => {
		if (!accountIdRef && technicalReviewArn && accounts.length > 0) {
			const account = accounts.find((a) => a.arn === technicalReviewArn);
			if (account && account.connectedAccounts.length > 0) {
				setConnectedAccountsDropDown(account.connectedAccounts);
				setAccountIdRef(account.connectedAccounts[0].accountIdRef);
			}
		}
	}, [technicalReviewArn, accounts, accountIdRef]);

	//Technical Review Account Dropdown Handle
  const handleTechnicalReviewAccount = useCallback((e: SelectChangeEvent<string>) => {
		const account = accounts.find((i: Account) => i.arn === e.target.value);
		if (account) {
			setConnectedAccountsDropDown(account.connectedAccounts);
			setAccountIdRef(account.connectedAccounts?.[0]?.accountIdRef);
			setTechnicalReviewArn(account.arn);
			setTechnicalAccountType(account.accounttype);
		}
	}, [accounts]);

	//Connected Account Dropdown Handle
	const handleConnectedAccount = useCallback((e: SelectChangeEvent<string>) => {
		setAccountIdRef(e.target.value as string);
		const account = accounts.find((i: Account) => i.arn === technicalReviewArn); //get the account that the connectedAccount belongs to
		if (account) {
			let automatePlus = false;
			//Check if connected account is part of the member_accounts or is the same as targetAccount
			if (
				account?.member_accounts.includes(e.target.value as string) ||
				technicalReviewArn.split(":")[4] === (e.target.value as string)
			) {
				automatePlus = true;
			} else {
				automatePlus = false;
			}
			//If connected Account is part of member account or if the targetAccount and connected account
			//are the same then set true to connectedAutomatePlus
			setConnectedAutomatePlus(automatePlus);
		}
	}, [accounts, technicalReviewArn]);

	const formik = useFormik({
		initialValues: {
			applicationName: "",
		},
		validationSchema,
		onSubmit: async (values) => {
			try {
				const response = await createMultiFrameworkMutation({
					variables: {
						arn: technicalReviewArn,
						standard: "Foundational Technical Review",
						frameworkName: values.applicationName,
						awsTechnicalReviewAccount: technicalReviewArn,
						connectedAccounts: getFilteredConnectedAccounts(),
					},
				});

				const result = response?.data?.createMultiFramework;

				if (result?.status === "COMPLETE") {
					setUnlockSteps((prevState) => ({
						...prevState,
						step2: true,
					}));

					const frameworkType = (technicalAccountType === "AUTOMATED" && connectedAutomatePlus)
						? "AUTOMATED"
						: technicalAccountType;

					changeTab(1, values.applicationName, technicalReviewArn, getFilteredConnectedAccounts(), frameworkType);
				} else {
					setAlerts([
						{
							severity: "error",
							msg: "Please use another Application Workflow name",
						},
					]);
				}
			} catch (error: any) {
				noticeError(error, "An unexpected error occurred while creating the application.");
				setAlerts([
					{
						severity: "error",
						msg: "An unexpected error occurred while creating the application.",
					},
				]);
			}
		}
	});

  return (
		<form onSubmit={formik.handleSubmit}>
			<Grid
				container
				sx={{ p: 2 }}
				spacing={2}
				alignItems="stretch"
				flexDirection={"column"}
				component={Paper}
				maxWidth="lg"
			>
				<Grid item xs md={12}>
					<Typography
						component={"span"}
						variant="h6"
						color="text.primary"
						paragraph
					>
						AUTOMATE+ is integrated with AWS native services to allow automated
						generation and updating of the assets required by AWS for evaluation
						of the Foundational Technical Review.
					</Typography>	
				</Grid>
				<Grid item xs md={12}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
							width: "inherit",
							p: 2,
							pl: 10,
							pr: 10,
							justifyContent: "space-between",
						}}
					>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
							}}
						>
							{steps.map((step, idx) => (
								<Typography
									key={idx}
									variant="subtitle1"
									color="text.secondary"
									paragraph
									sx={{ mb: 0 }}
								>
									STEP {idx + 1}: {step}
								</Typography>
							))}
						</Box>
						<Box>
							<Button
								sx={{
									"&:hover": {
										color: "primary.main",
									},
								}}
								href="https://partnercentral.awspartner.com/ContentFolderPartner?id=0690h00000BGRJhAAP"
								target="_blank"
								variant="outlined"
							>
								Partner Central Official AWS FTR Guide
							</Button>
						</Box>
					</Box>
				</Grid>
				<Grid item xs md={12}>
					<Typography
						component={"span"}
						variant="h6"
						color="text.primary"
						paragraph
					>
						Create a Foundational Technical Review Application.
					</Typography>
				</Grid>
				<Grid item md={12}>
					<Typography variant="body2">
						Foundational Technical Reviews are conducted for each individual application. 
						Please select the AWS account associated with the application you would like to review.
					</Typography>
				</Grid>
				<Grid item md={6}>
					<Box sx={{ minWidth: 120 }}>
						<FormControl fullWidth>
							<InputLabel id="aws-technical-review-account">
								AWS Technical Review Account
							</InputLabel>
							<Select
								required
								id="aws-technical-review-account-select"
								name="aws-technical-review-account"
								value={technicalReviewArn}
								label="AWS Technical Review Account"
								onChange={handleTechnicalReviewAccount}
							>
								{accounts.length === 0 ? (
									<MenuItem disabled>No AWS accounts available</MenuItem>
								) : (
									accounts.map((a: Account) => (
										<MenuItem key={a.arn} value={a.arn}>
											{`${a.accessnickname} - ${a.arn.split(":")[4]}`}
										</MenuItem>
									))
								)}
							</Select>
						</FormControl>
					</Box>
				</Grid>
				<Grid item md={6}/>
				{connectedAccountsDropDown.length > 1 ? (
					<>
						<Grid item md={12}>
							<Typography variant="body2">
								You have selected an AWS parent account which has multiple
								AWS sub-accounts. Which AWS sub-account contains the
								workload you want to review? Please select from the drop
								down below.
							</Typography>
						</Grid>
						<Grid item md={6}>
							<Box sx={{ minWidth: 120 }}>
								<FormControl fullWidth>
									<InputLabel id="aws-connected-account">
										AWS Connected Account
									</InputLabel>
									<Select
										required
										id="aws-connected-account-select"
										name="aws-connected-account"
										value={accountIdRef}
										label="AWS Connected Account"
										onChange={handleConnectedAccount}
									>
										{uniqueConnectedAccountIds
											.map((accountIdRef: string) => {
												return (
													<MenuItem
														key={`${accountIdRef}`}
														value={accountIdRef}
													>{`${accountIdRef}`}</MenuItem>
												);
											})}
									</Select>
								</FormControl>
							</Box>
						</Grid>
						<Grid item md={6} />
					</>
				) : null}
				<Grid item md={6}>
					<Box sx={{ minWidth: 120 }}>
						<FormControl fullWidth>
							<TextField
								fullWidth
								id="ftr-workflow-create-application-name"
								placeholder="Application Name"
								label="Create Application Name"
								value={formik.values["applicationName"]}
								error={
									formik.touched["applicationName"] &&
									Boolean(formik.errors["applicationName"])
								}
								helperText={
									formik.touched["applicationName"] &&
									formik.errors["applicationName"]
										? String(formik.errors["applicationName"])
										: ""
								}
								onChange={(e) => formik.setFieldValue("applicationName", e.target.value)}
								onBlur={formik.handleBlur}
							/>
						</FormControl>
					</Box>
				</Grid>
				<Grid item xs md={4} justifyContent="flex-end">
					<LoadingButton
						id="ftr-workflow-create-btn"
						variant="contained"
						color="secondary"
						type="submit"
						disabled={loading}
						loading={loading}
						sx={{
							width: 157,
							height: 40,
							bgcolor: "secondary.main",
							"&:hover": {
								bgcolor: "secondary.main",
								color: "secondary.contrastText",
							},
						}}
						loadingIndicator={
							<CircularProgress color={"secondary"} size={22} />
						}
					>
						Create
					</LoadingButton>
				</Grid>
				<Grid item md={8}>
					<CardMedia
						component="img"
						sx={{ pt: 2, pb: 2, width: "75%" }}
						image={ftrimage}
						alt="6pillars FTR Automation"
					/>
				</Grid>
			</Grid>
		</form>
	)
};

export default CreateFTRWorkflow;
